/**
 * scheduleSearch.ts
 * Free-text matching for the Machine Schedule search box (UX overhaul — M5).
 *
 * A query is split into whitespace tokens; every token must appear (AND) in the
 * target's searchable text (case-insensitive substring). This makes multi-word
 * queries like "rock M-01 vahid" progressively narrow the results.
 */

import type { MachineBookingCalendar } from '../types/schedule';

/** Split a query into lowercase tokens. */
function tokenize(query: string): string[] {
  return query.toLowerCase().split(/\s+/).filter(Boolean);
}

/** Join defined fields into one lowercase haystack. */
function haystack(parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).join(' ').toLowerCase();
}

/**
 * Does a booking match the query? Searches across machine code/name/section,
 * task name, event type, source, status, notes, work-order number, quote
 * number, and every assignee's name.
 */
export function bookingMatchesQuery(b: MachineBookingCalendar, query: string): boolean {
  const tokens = tokenize(query);
  if (tokens.length === 0) return true;
  const hay = haystack([
    b.MachineCode,
    b.MachineName,
    b.MachineLabSection,
    b.TaskName,
    b.EventType,
    b.Source,
    b.Status,
    b.Notes,
    b.WorkOrderNo,
    b.WorkOrderName,
    b.QuoteNumber,
    b.QuoteTitle,
    // Bars now show project/client names, so search can find them too (2026-06-23).
    b.ProjectName,
    b.ClientName,
    ...b.Assignees.map((a) => a.Name),
  ]);
  return tokens.every((t) => hay.includes(t));
}

/** Does a machine (row) itself match the query — by code, name, or lab section? */
export function machineMatchesQuery(
  machine: { code: string; name: string; labSection: string | null },
  query: string,
): boolean {
  const tokens = tokenize(query);
  if (tokens.length === 0) return true;
  const hay = haystack([machine.code, machine.name, machine.labSection]);
  return tokens.every((t) => hay.includes(t));
}
